import React, { useState, useEffect } from 'react';
import { FaSpinner, FaCalendar, FaUser } from 'react-icons/fa';
import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import './BlogSection.css';

const FALLBACK_BLOGS = [
  {
    _id: 1,
    title: "5 Tips for Finding the Best Cab Service",
    description: "Learn how to choose the right cab service for your daily commute. We share insider tips on what makes a great ride experience.",
    category: "Travel Tips",
    featuredImage: "https://images.unsplash.com/photo-1500595046891-13d9a48c2c44?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Rajesh Kumar",
    authorImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-15"
  },
  {
    _id: 2,
    title: "Safety First: Your Guide to Secure Rides",
    description: "Safety is our top priority. Discover the comprehensive safety measures in place to protect you on every journey.",
    category: "Safety",
    featuredImage: "https://images.unsplash.com/photo-1519904981063-b0cf448d479e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Priya Sharma",
    authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-20"
  },
  {
    _id: 3,
    title: "How to Save Money on Daily Commutes",
    description: "Discover smart ways to save on your daily rides. Our money-saving tips can help you cut commute costs significantly.",
    category: "Budget Tips",
    featuredImage: "https://images.unsplash.com/photo-1566776615325-7c321b0b5a7d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=90",
    authorName: "Amit Patel",
    authorImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=90",
    publishDate: "2024-01-22"
  }
];

const BlogSection = () => {
  const [blogs, setBlogs] = useState(FALLBACK_BLOGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/blogs/public`, { params: { limit: 12 } });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        setBlogs(response.data.data);
      } else {
        // Use fallback if no blogs returned
        setBlogs(FALLBACK_BLOGS);
      }
    } catch (error) {
      console.warn('Error fetching blogs, using fallback data:', error?.message);
      // Use fallback on any error
      setBlogs(FALLBACK_BLOGS);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
   <section className="bg-white text-white py-20 px-6">
  <div className="max-w-7xl mx-auto">

    {/* Heading */}
    <div className="text-center mb-14">
      <h2 className="text-4xl font-bold">
        <span className="text-blue-500">Cab</span>
        <span className="text-orange-500">Zigo</span>
        <span className="text-black" > in media</span>
      </h2>
      <div className="w-60 h-2 bg-gradient-to-r from-gray-200 to-blue-600 rounded-full mx-auto mt-3"></div>
    </div>

    {/* Loading */}
    {loading && (
      <div className="flex justify-center py-16">
        <FaSpinner className="animate-spin text-blue-500 text-4xl" />
      </div>
    )}

    {/* Error */}
    {error && (
      <div className="text-center text-red-500 py-10">
        {error}
      </div>
    )}

    {/* Blog Grid */}
    {!loading && !error && blogs.length > 0 && (
      <div className="grid md:grid-cols-3 gap-10">

        {blogs.map((blog) => (
          <div key={blog._id} className="group">

            {/* Image */}
            <div className="relative overflow-hidden rounded-xl">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-64 object-cover group-hover:scale-105 transition duration-500"
                onError={(e) => {
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='18' fill='%236b7280'%3EBlog Image%3C/text%3E%3C/svg%3E";
                }}
              />

              {/* Category Badge */}
              <span className="absolute top-4 right-4 bg-black/70 backdrop-blur px-4 py-1 text-xs rounded-md border border-white/30">
                {blog.category}
              </span>
            </div>

            {/* Author */}
            <div className="flex items-center gap-3 mt-6">
              <img
                src={blog.authorImage}
                alt={blog.authorName}
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='100%25' height='100%25' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3EAuthor%3C/text%3E%3C/svg%3E";
                }}
              />
              <div>
                <p className="font-semibold text-sm">
                  {blog.authorName}
                </p>
                <p className="text-gray-400 text-xs">
                  {formatDate(blog.publishDate)}
                </p>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold mt-4 group-hover:text-blue-500 transition line-clamp-2">
              {blog.title}
            </h3>

            {/* Description */}
            <p className="text-gray-400 mt-3 text-sm leading-relaxed line-clamp-3">
              {blog.description}
            </p>

          </div>
        ))}

      </div>
    )}

    {/* Empty */}
    {!loading && !error && blogs.length === 0 && (
      <div className="text-center py-12 text-gray-400">
        No blog posts available.
      </div>
    )}

    {/* Navigation Arrows */}
    <div className="flex gap-4 mt-12">
      <button className="w-12 h-12 bg-gray-800 rounded-md flex items-center justify-center hover:bg-gray-700 transition text-white">
        ←
      </button>
      <button className="w-12 h-12 bg-gray-800 rounded-md flex items-center justify-center hover:bg-gray-700 transition text-white">
        →
      </button>
    </div>

  </div>
</section>
  );
};

export default BlogSection;
